export default function CoralSpringsDestinationsSection() {
  const stats = [
    {
      value: "55%",
      label: "of Coral Springs requests are Caribbean cruise or resort departures with under 3 weeks notice",
    },
    {
      value: "2–4 weeks",
      label: "average notice for business trips to Latin America from Coral Springs professionals",
    },
    {
      value: "Mar–Jun",
      label: "peak travel season — spring break, bar/bat mitzvah trips, and summer vacation bookings",
    },
    {
      value: "48-hour",
      label: "shortest cruise departure window we've accommodated for a Coral Springs family",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "The most common destination for Coral Springs travelers — whether by cruise from nearby Port Everglades or by direct flight from FLL to Nassau, Montego Bay, or San Juan. Families from the Sportsplex community, religious groups, and retirees all regularly sail to the Bahamas, Jamaica, the Cayman Islands, and the Eastern Caribbean. All cruise passengers and most air travelers need valid passports.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Israel",
      context: "The Jewish community in Coral Springs and neighboring Parkland travels to Israel for bar and bat mitzvah ceremonies, family visits, heritage tours, and religious pilgrimages. These trips are often planned years in advance with group tour operators, but passport validity is frequently overlooked until the travel agent requests documentation. Israel requires passports valid for at least six months beyond the planned departure date.",
      urgency: "Group travel deposits are typically non-refundable within 60 days — passport delays can cost thousands in lost reservations",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico",
      context: "Cancun, Riviera Maya, and Cozumel are staple beach vacation destinations for Coral Springs families seeking all-inclusive resorts within a short flight from FLL. Business travelers also fly to Mexico City and Guadalajara for corporate meetings. Mexico requires six months of passport validity for entry, and FLL offers direct and connecting service to Cancun and Mexico City year-round.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February and March",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada",
      context: "Toronto and Montreal draw Coral Springs business travelers for finance, healthcare, and tech conferences. Canadian ski destinations like Whistler and Banff attract winter sports enthusiasts from the Coral Springs Sportsplex community. Canada requires a valid passport for all air arrivals, and many Coral Springs families travel with minors whose five-year passports expire faster than expected.",
      urgency: "Business conference registration deadlines often require confirmed travel 4–6 weeks in advance",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "United Kingdom & Europe",
      context: "London is a frequent destination for Coral Springs professionals attending conferences and for families planning summer cultural tours. Paris, Rome, Barcelona, and Amsterdam draw affluent Coral Springs families on educational vacations. The Schengen Area requires three months of validity beyond your planned departure, and many European destinations are increasingly popular with the city's younger professional demographic.",
      urgency: "Summer tour deposits and villa rental agreements are typically non-refundable with short cancellation windows",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Latin America",
      context: "Bogota, Medellin, Cartagena, and Panama City draw Coral Springs business travelers in healthcare, finance, and real estate. Sao Paulo and Rio de Janeiro attract corporate visitors from Fort Lauderdale-based companies with Coral Springs commuters. Costa Rica and Belize attract eco-tourism and adventure travelers from the younger professional demographic. Each country has its own passport validity requirements, typically six months.",
      urgency: "Business trips to Latin America are often booked with 2–4 weeks of notice — too short for standard processing",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Coral Springs Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Coral Springs
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Coral Springs travelers head to the Caribbean, Israel, Mexico, Europe, and Latin America for a mix of family cruises, religious pilgrimages, business trips, and educational vacations. These are the destinations that create the most passport urgency for our Coral Springs clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout South Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Parkland", href: "/cities/parkland" },
              { name: "Plantation", href: "/cities/plantation" },
              { name: "Weston", href: "/cities/weston" },
              { name: "Boca Raton", href: "/cities/boca-raton" },
              { name: "West Palm Beach", href: "/cities/west-palm-beach" },
              { name: "Fort Lauderdale", href: "/cities/fort-lauderdale" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}